import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import store, { persistor } from './store/redux/store';

// Components
import ProductFactory from './components/legacy/product-factory/ProductFactory';

// Styling
import { PersistGate } from 'redux-persist/lib/integration/react';
import RiskAnalysis from '../../risk-analysis/src/pages';
import { Center } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { isRiskAnalysisFlow } from '../../common/src/utils/globalVariables';
import Can from '../../app/src/features/permissions/Can';
import { ErrorBoundary } from '@sentry/react';
import LoadingScreen from '../../common/src/components/LoadingScreen';
import { Offers } from './features/offers/pages';
import VillasureOffers from './pages/villasure/offers';
import VillasureFinalize from './pages/villasure/finalize';
import VillasureContract from './pages/villasure/contract';
import PiaBoatOffers from './pages/boat/offers';
import SocialRoute from '../../app/src/features/routing/components/SocialRoute';
import BikmoOffers from './pages/bikmo/offers';
import BikmoContract from './pages/bikmo/contract';
import BikmoFinalize from './pages/bikmo/finalize';
import PiaBoatFinalize from './pages/boat/finalize';
import CyclingVlaanderenOffers from './pages/cycling-vlaanderen/offers';
import CyclingVlaanderenContract from './pages/cycling-vlaanderen/contract';
import CyclingVlaanderenFinalize from './pages/cycling-vlaanderen/finalize';
import { isSocialLoginUrl } from '../../app/src/features/authentication/constants/SocialAuth';
import CompanyCheckup from './pages/checkup/company-checkup';
import CustomerCheckup from './pages/checkup/customer-checkup';
import CfVerzekeringen from './pages/cf-verzekeringen';
import AskaTravelOffers from './pages/aska/offers';
import AskaTravelContract from './pages/aska/contract';
import CampaignsFinalizationView from './pages/campaigns-finalization';
import DefaultSalesConversation from './pages/default/DefaultSalesConversation';
import TechniekNederlandFinalize from './pages/techniek-nederland/finalize';

const ProductFactoryRouter: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate
          loading={
            <Center height="100vh" w="100%">
              <LoadingScreen />
            </Center>
          }
          persistor={persistor}
        >
          <Helmet>
            {isRiskAnalysisFlow() ? (
              <title>{t('RISK-ANALYSIS')} | WeGroup</title>
            ) : (
              <title>{t('Advisory conversation')} | WeGroup</title>
            )}
          </Helmet>

          <Switch>
            <Route
              path={['/product']}
              render={({ match: { url } }) =>
                isSocialLoginUrl ? (
                  <Route
                    path={[`${url}/session`]}
                    render={() => <ProductFactory url={`${url}/session`} />}
                  />
                ) : (
                  <Can I="use" a="advisory_conversation">
                    <Route
                      path={[`${url}/session`]}
                      render={() => <ProductFactory url={`${url}/session`} />}
                    />
                  </Can>
                )
              }
            />
            {/**
             * Temporary risk analysis route
             * Will be removed when we have a "Flows" file structure
             */}
            <Route
              path="/risk-analysis"
              render={({ match: { url } }) => (
                <Route
                  path={url}
                  render={() => <RiskAnalysis url={`${url}`} />}
                />
              )}
            />

            <Route
              path="/sales-conversation/offers"
              render={() => (
                <SocialRoute>
                  <Offers />
                </SocialRoute>
              )}
            />
            {/* TECHNIEK NEDERLAND */}
            <Route
              path="/sales-conversation/techniek-nederland/finalize"
              render={() => (
                <SocialRoute>
                  <TechniekNederlandFinalize />
                </SocialRoute>
              )}
            />
            {/* VILLASURE */}
            <Route
              path="/sales-conversation/villasure/offers"
              render={() => (
                <SocialRoute>
                  <VillasureOffers />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/villasure/contracts"
              render={() => (
                <SocialRoute>
                  <VillasureContract />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/villasure/finalize"
              render={() => (
                <SocialRoute>
                  <VillasureFinalize />
                </SocialRoute>
              )}
            />

            {/* PIA BOAT */}
            <Route
              path="/sales-conversation/boat/offers"
              render={() => (
                <SocialRoute>
                  <PiaBoatOffers />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/boat/finalize"
              render={() => (
                <SocialRoute>
                  <PiaBoatFinalize />
                </SocialRoute>
              )}
            />

            {/* BIKMO */}
            <Route
              path="/sales-conversation/bikmo/offers"
              render={() => (
                <SocialRoute>
                  <BikmoOffers />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/bikmo/contracts"
              render={() => (
                <SocialRoute>
                  <BikmoContract />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/bikmo/finalize"
              render={() => (
                <SocialRoute>
                  <BikmoFinalize />
                </SocialRoute>
              )}
            />

            {/* COMPANY CHECKUP */}
            <Route
              path="/sales-conversation/company-checkup"
              exact
              render={() => (
                <SocialRoute>
                  <CompanyCheckup />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/company-checkup/finalize"
              render={() => (
                <SocialRoute>
                  <CampaignsFinalizationView />
                </SocialRoute>
              )}
            />

            {/* CUSTOMER CHECKUP */}
            <Route
              path="/sales-conversation/customer-checkup"
              exact
              render={() => (
                <SocialRoute>
                  <CustomerCheckup />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/customer-checkup/finalize"
              render={() => (
                <SocialRoute>
                  <CampaignsFinalizationView />
                </SocialRoute>
              )}
            />
            {/* CF VERZEKERINGEN */}
            <Route
              path="/sales-conversation/cf-verzekeringen"
              exact
              render={() => (
                <SocialRoute>
                  <CfVerzekeringen />
                </SocialRoute>
              )}
            />
            {/* DEFAULT FLOW */}
            <Route
              path="/sales-conversation/default/finalize"
              exact
              render={() => (
                <SocialRoute>
                  <CampaignsFinalizationView />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/default/:flowId"
              exact
              render={() => (
                <SocialRoute>
                  <DefaultSalesConversation />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/cf-verzekeringen/finalize"
              render={() => (
                <SocialRoute>
                  <CampaignsFinalizationView />
                </SocialRoute>
              )}
            />
            {/* ASKA TRAVEL */}
            <Route
              path="/sales-conversation/aska-travel/offers"
              render={() => (
                <SocialRoute>
                  <AskaTravelOffers />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/aska-travel/contract"
              exact
              render={() => (
                <SocialRoute>
                  <AskaTravelContract />
                </SocialRoute>
              )}
            />

            {/* CYCLING VLAANDEREN */}
            <Route
              path="/sales-conversation/cycling-vlaanderen/offers"
              render={() => (
                <SocialRoute>
                  <CyclingVlaanderenOffers />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/cycling-vlaanderen/contracts"
              render={() => (
                <SocialRoute>
                  <CyclingVlaanderenContract />
                </SocialRoute>
              )}
            />
            <Route
              path="/sales-conversation/cycling-vlaanderen/finalize"
              render={() => (
                <SocialRoute>
                  <CyclingVlaanderenFinalize />
                </SocialRoute>
              )}
            />
          </Switch>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
};

export default ProductFactoryRouter;
